import { IconButton } from "@medoma/components/Button/Button";
import { useDialog } from "@medoma/components/Dialog/useDialog";
import BinIcon from "@medoma/components/icons/BinIcon";
import { useLingui } from "@lingui/react/macro";
import {
  setGroupNotInvoiceable,
  setOccurrenceNotInvoiceable,
} from "@/api/Invoicing";
import { useMutation, useQueryClient } from "@tanstack/react-query";

export const Actions = ({
  activityOccurrenceOrGroupId,
}: {
  activityOccurrenceOrGroupId:
    | {
        groupId: string;
        occurrenceId?: never;
      }
    | {
        groupId?: never;
        occurrenceId: string;
      };
}) => {
  const { t } = useLingui();
  const { confirm } = useDialog();
  const queryClient = useQueryClient();
  const { groupId, occurrenceId } = activityOccurrenceOrGroupId;

  const { mutate: mutateSetGroupNotInvoiceable } = useMutation({
    mutationFn: (groupId: string) => setGroupNotInvoiceable(groupId),
    onSuccess: async () => {
      return queryClient.invalidateQueries();
    },
  });

  const { mutate: mutateSetOccurrenceNotInvoiceable } = useMutation({
    mutationFn: (occurrenceId: string) =>
      setOccurrenceNotInvoiceable(occurrenceId),
    onSuccess: async () => {
      return queryClient.invalidateQueries();
    },
  });

  const removeActivityOccurrences = async () => {
    const itemTypeToRemove: "group" | "activityOccurrence" = groupId
      ? "group"
      : "activityOccurrence";

    switch (itemTypeToRemove) {
      case "group": {
        const removeGroupIsConfirmed = await confirm({
          title: t`Ta bort besöket från faktureringsunderlaget`,
          message: t`Är du säker på att du vill ta bort besöket från faktureringsunderlaget? Åtgärden går inte att ångra.`,
        });

        if (removeGroupIsConfirmed && groupId) {
          mutateSetGroupNotInvoiceable(groupId);
        }
        break;
      }

      case "activityOccurrence": {
        const removeActivityOccurrenceIsConfirmed = await confirm({
          title: t`Ta bort aktivitetstillfället från faktureringsunderlaget`,
          message: t`Är du säker på att du vill ta bort aktivitetstillfället från faktureringsunderlaget? Åtgärden går inte att ångra.`,
        });

        if (removeActivityOccurrenceIsConfirmed && occurrenceId) {
          mutateSetOccurrenceNotInvoiceable(occurrenceId);
        }
        break;
      }

      default:
        itemTypeToRemove satisfies never;
    }
  };

  return (
    <IconButton
      aria-label={t`Ta bort från faktureringsunderlaget`}
      onClick={removeActivityOccurrences}
    >
      <BinIcon />
    </IconButton>
  );
};
